import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Label } from 'components/Label';
import { TooltipProvider } from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { emailExists } from 'utils/emails';
import { cn } from 'utils/shadcn';

import EmailDropdownOption from './EmailDropdownOption';
import EmailPillTooltip from './EmailPillTooltip';
import { EmailRecipient, EmailRecipientsInputProps } from './types';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EmailRecipientsInput({
  name,
  label,
  value,
  onChange,
  customOptions = [],
  placeholder = 'Enter email address',
  required = false,
  allowMultiple = true,
  disabled = false,
  error,
  maxEmails,
  aiHintTooltip,
}: EmailRecipientsInputProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [validationError, setValidationError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Convert value array to EmailRecipient objects for display
  const selectedRecipients = useMemo(() => {
    return value.map((email): EmailRecipient => {
      // Check if this email matches a custom option
      const matchingOption = customOptions.find(
        (option) => option.email === email
      );
      if (matchingOption) {
        return matchingOption;
      }

      return { email };
    });
  }, [value, customOptions]);

  // Filter custom options based on search term and exclude already selected emails
  const filteredOptions = useMemo(() => {
    // First, filter out already selected emails
    const availableOptions = customOptions.filter(
      (option) => !emailExists(option.email, value)
    );

    if (!searchTerm.trim()) {
      return availableOptions.slice(0, 6); // Show first 6 options when no search
    }

    const term = searchTerm.toLowerCase().trim();
    return availableOptions.filter((option) => {
      const nameMatch = option.name?.toLowerCase().includes(term);
      const emailMatch = option.email.toLowerCase().includes(term);
      return nameMatch || emailMatch;
    });
  }, [searchTerm, customOptions, value]);

  const exactMatchOption = useMemo(() => {
    if (!searchTerm.trim()) return null;
    return customOptions.find(
      (option) =>
        option.email.toLowerCase() === searchTerm.toLowerCase().trim() &&
        !emailExists(option.email, value)
    );
  }, [searchTerm, customOptions, value]);

  const validateEmail = useCallback((email: string): boolean => {
    return EMAIL_REGEX.test(email.trim());
  }, []);

  // Check if the search term is a valid email that can be added
  const validEmailToAdd = useMemo(() => {
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      return null;
    }

    // Don't show if it's already selected (case-insensitive check)
    if (emailExists(trimmed, value)) {
      return null;
    }

    // Don't show if it exactly matches a custom option (that option will show instead)
    const hasExactMatch = customOptions.some(
      (option) => option.email.toLowerCase() === trimmed.toLowerCase()
    );
    if (hasExactMatch) {
      return null;
    }

    // Only show if it's a valid email
    if (!validateEmail(trimmed)) {
      return null;
    }

    return trimmed;
  }, [searchTerm, value, customOptions, validateEmail]);

  // Parse comma/semicolon/whitespace separated emails
  const parseEmailsFromText = useCallback((text: string): string[] => {
    return text
      .split(/[;,]\s*|\s+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
  }, []);

  const addEmails = useCallback(
    (emails: string[]) => {
      const validEmails: string[] = [];
      const invalidEmails: string[] = [];

      emails.forEach((email) => {
        const trimmedEmail = email.trim();
        if (validateEmail(trimmedEmail)) {
          // Check if email is already selected (case-insensitive check)
          if (!emailExists(trimmedEmail, value)) {
            validEmails.push(trimmedEmail);
          }
        } else {
          invalidEmails.push(trimmedEmail);
        }
      });

      if (invalidEmails.length > 0) {
        setValidationError(`Invalid email(s): ${invalidEmails.join(', ')}`);
        setTimeout(() => setValidationError(''), 3000);
      }

      if (validEmails.length > 0) {
        let newEmails = [...value, ...validEmails];
        if (maxEmails && newEmails.length > maxEmails) {
          newEmails = newEmails.slice(0, maxEmails);
        }
        onChange(newEmails);
      }
    },
    [value, validateEmail, onChange, maxEmails]
  );

  const removeEmail = useCallback(
    (emailToRemove: string) => {
      const newEmails = value.filter((email) => email !== emailToRemove);
      onChange(newEmails);
    },
    [value, onChange]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      setValidationError('');
      if (!isDropdownOpen) {
        setIsDropdownOpen(true);
      }
    },
    [isDropdownOpen]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const trimmedSearchTerm = searchTerm.trim();

        if (trimmedSearchTerm) {
          // If we have an exact match in options, use that
          if (exactMatchOption) {
            addEmails([exactMatchOption.email]);
          } else {
            // Check if input contains comma-separated emails and all are valid
            const potentialEmails = parseEmailsFromText(trimmedSearchTerm);
            const allValid = potentialEmails.every((email) =>
              validateEmail(email)
            );

            if (potentialEmails.length > 1 && allValid) {
              // Multiple valid emails - add them all
              addEmails(potentialEmails);
            } else {
              // Single email or invalid multi-email input - treat as one entry
              addEmails([trimmedSearchTerm]);
            }
          }
          setSearchTerm('');
          setIsDropdownOpen(false);
        }
      } else if (e.key === 'Backspace' && !searchTerm && value.length > 0) {
        // Remove last email when backspace is pressed on empty input
        removeEmail(value[value.length - 1]);
      } else if (e.key === 'Escape') {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    },
    [
      searchTerm,
      exactMatchOption,
      addEmails,
      removeEmail,
      value,
      parseEmailsFromText,
      validateEmail,
    ]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      const emails = parseEmailsFromText(pastedText);

      if (emails.length > 1) {
        // Multiple emails pasted - add them all
        addEmails(emails);
        setSearchTerm('');
        setIsDropdownOpen(false);
      } else if (emails.length === 1) {
        // Single email pasted - set as search term
        setSearchTerm(emails[0]);
        setIsDropdownOpen(true);
      }
    },
    [parseEmailsFromText, addEmails]
  );

  const handleFocus = useCallback(() => {
    setIsDropdownOpen(true);
  }, []);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        // Process any pending email input before closing, same as Enter key behavior
        const trimmedSearchTerm = searchTerm.trim();
        if (trimmedSearchTerm) {
          // If we have an exact match in options, use that
          if (exactMatchOption) {
            addEmails([exactMatchOption.email]);
          } else {
            // Otherwise, treat as manual entry
            addEmails([trimmedSearchTerm]);
          }
          setSearchTerm('');
        }
        setIsDropdownOpen(false);
      }
    },
    [searchTerm, exactMatchOption, addEmails]
  );

  const handleOptionSelect = useCallback(
    (option: EmailRecipient) => {
      addEmails([option.email]);
      setSearchTerm('');
      setIsDropdownOpen(false);
      inputRef.current?.focus();
    },
    [addEmails]
  );

  // Setup click outside listener
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const displayError = error || validationError;

  return (
    <TooltipProvider>
      <Flex direction='col' gap='xs' className='w-full'>
        {label && (
          <Label
            name={name || ''}
            required={required}
            showAIHint={customOptions.length > 0 || value.length > 0}
            aiHintTooltip={aiHintTooltip}
          >
            {label}
          </Label>
        )}

        <Flex
          ref={containerRef}
          wrap='wrap'
          className={cn(
            'w-full relative gap-1.5 border rounded-[4px] p-1.5 text-xs items-center',
            disabled
              ? 'border-neutral-input-disabled-border bg-neutral-input-disabled-bg cursor-not-allowed'
              : 'border-neutral-input-border focus-within:border-brand-500 bg-neutral-input-bg cursor-text'
          )}
          onClick={() => !disabled && inputRef.current?.focus()}
        >
          {/* Selected email pills */}
          {selectedRecipients.map((recipient) => (
            <EmailPillTooltip
              key={recipient.email}
              recipient={recipient}
              onRemove={disabled ? undefined : removeEmail}
              disabled={disabled}
            />
          ))}

          {/* Input field */}
          <input
            ref={inputRef}
            type='text'
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={handleFocus}
            placeholder={
              selectedRecipients.length === 0
                ? placeholder
                : allowMultiple
                  ? 'Add another email'
                  : ''
            }
            disabled={disabled || (!allowMultiple && value.length > 0)}
            aria-label={`Enter email addresses${allowMultiple ? ' (multiple allowed)' : ''}`}
            className={cn(
              'grow p-1 outline-none rounded-[4px] text-xs min-w-[120px]',
              disabled
                ? 'bg-transparent text-neutral-input-disabled-text placeholder-neutral-400 cursor-not-allowed'
                : 'bg-transparent hover:bg-neutral-input-bg text-neutral-input-text placeholder-neutral-400 cursor-text'
            )}
          />

          {/* Dropdown */}
          {isDropdownOpen &&
            (filteredOptions.length > 0 || validEmailToAdd) && (
              <div className='absolute top-full left-0 right-0 mt-1 z-10 bg-neutral-50 border border-neutral-400 rounded-[4px] shadow-lg max-h-60 overflow-y-auto w-full'>
                {/* Show valid email suggestion first if it exists */}
                {validEmailToAdd && (
                  <EmailDropdownOption
                    key={`add-${validEmailToAdd}`}
                    option={{ email: validEmailToAdd, name: 'Add email' }}
                    isHighlighted={true}
                    onSelect={handleOptionSelect}
                  />
                )}

                {/* Show filtered custom options */}
                {filteredOptions.map((option) => (
                  <EmailDropdownOption
                    key={option.email}
                    option={option}
                    isHighlighted={exactMatchOption?.email === option.email}
                    onSelect={handleOptionSelect}
                  />
                ))}
              </div>
            )}
        </Flex>

        {/* Error message */}
        {displayError && (
          <Typography variant='body-xs' className='text-error-500'>
            {displayError}
          </Typography>
        )}
      </Flex>
    </TooltipProvider>
  );
}
